import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoSpreadsheetDownload() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">Download</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Spreadsheet Download: Free Ready-to-Use Files</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">Download ready-to-use BaseTao spreadsheet files and start organizing your shopping immediately.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Get Your BaseTao Spreadsheet</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">The <strong className="text-foreground-900">BaseTao Spreadsheet download</strong> process is designed to be as simple as possible. While we do not host downloadable files on this site, you can easily create your own BaseTao Spreadsheet using any spreadsheet software — Google Sheets, Microsoft Excel, Apple Numbers, or LibreOffice Calc all work perfectly.</p>
              <p className="text-foreground-600 leading-relaxed">The key is setting up the right column structure. Once you have the framework in place, you can customize it to fit your exact shopping workflow. See our <Link to="/basetao-spreadsheet-template/" className="text-primary-600 hover:text-primary-700 font-medium">template guide</Link> for the recommended column layout.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">How to Create Your Spreadsheet File</h2>
              <div className="space-y-4">
                {[{s:'1',t:'Choose Your Software',d:'Open Google Sheets (free, cloud-based), Microsoft Excel, or any spreadsheet application you prefer. Google Sheets is recommended for its cloud sync and accessibility from any device.'},{s:'2',t:'Set Up Column Headers',d:'Create headers: Product Name, Category, Price, Shipping Estimate, Warehouse Status, Final Cost, Notes. Freeze the header row so it stays visible while scrolling.'},{s:'3',t:'Add Formatting',d:'Format the Price and Shipping columns as currency. Add data validation dropdowns for Warehouse Status (Arrived, Inspected, Stored, Shipped) to keep entries consistent.'},{s:'4',t:'Create Summary Row',d:'Add SUM formulas at the bottom of price and shipping columns. Create a Grand Total cell that adds both together for your complete spending overview.'}].map((item,i)=>(
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">{item.s}</div>
                    <div>
                      <h3 className="font-heading text-sm text-foreground-900 mb-1">{item.t}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Sync Your Spreadsheet with Shopping</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">The best way to use your downloaded or created BaseTao Spreadsheet is to keep it open alongside the main site. As you browse categories like Shoes, Hoodies, Jackets, and Accessories, add interesting products directly to your sheet.</p>
              <p className="text-foreground-600 leading-relaxed">For a complete walkthrough of using your spreadsheet effectively, follow our <Link to="/basetao-spreadsheet-tutorial/" className="text-primary-600 hover:text-primary-700 font-medium">step-by-step tutorial</Link>. And if you need the column structure reference, check the <Link to="/basetao-spreadsheet/" className="text-primary-600 hover:text-primary-700 font-medium">full spreadsheet guide</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Start Building Your Spreadsheet</h3>
              <p className="text-foreground-600 text-sm mb-4">Open Google Sheets or Excel now, browse BaseTao products, and begin filling in your first rows.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">Browse Products <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-spreadsheet-template/" className="hover:text-primary-600 transition-colors">Template Guide</Link>
              <Link to="/basetao-spreadsheet-tutorial/" className="hover:text-primary-600 transition-colors">Tutorial</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Spreadsheet Download: Free Ready-to-Use Files',
            description: 'Download ready-to-use BaseTao spreadsheet files and start organizing your shopping immediately.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-spreadsheet-download/',
            },
          }),
        }}
      />
    </div>
  );
}